import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircleFilled,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
  LockOutlined,
  MailOutlined,
  ReloadOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import adminApi from "../api/adminApi";
import ConfirmDialog from "./ConfirmDialog";

const emptyForm = { email: "", password: "", confirmPassword: "" };

export default function UserRegistration() {
  const [form, setForm] = useState(emptyForm);
  const [visible, setVisible] = useState({ password: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");
  const [query, setQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError("");
    try {
      setUsers((await adminApi.getUsers()) || []);
    } catch (err) {
      setUsersError(adminApi.getErrorMessage(err, "Registered users could not be loaded."));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const validEmail = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()), [form.email]);
  const checks = useMemo(() => [
    { label: "Valid email address", met: validEmail },
    { label: "At least 8 characters", met: form.password.length >= 8 },
    { label: "Passwords match", met: Boolean(form.confirmPassword) && form.password === form.confirmPassword },
  ], [form, validEmail]);
  const filteredUsers = useMemo(() => users.filter((user) =>
    user.email?.toLowerCase().includes(query.trim().toLowerCase())), [users, query]);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
    setSuccess("");
  }

  async function submit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!validEmail) return setError("Enter a valid email address.");
    if (form.password.length < 8) return setError("The password must contain at least 8 characters.");
    if (form.password !== form.confirmPassword) return setError("The password and confirmation do not match.");

    setSaving(true);
    try {
      const result = await adminApi.registerUser(form.email.trim().toLowerCase(), form.password);
      setForm(emptyForm);
      setSuccess(result.message);
      await loadUsers();
    } catch (err) {
      setError(adminApi.getErrorMessage(err, "The administrator account could not be created."));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    const user = deleteTarget;
    if (!user || deleting) return;
    setDeleting(true);
    setError("");
    setSuccess("");
    try {
      const result = await adminApi.deleteUser(user._id);
      setUsers((current) => current.filter((item) => item._id !== user._id));
      setSuccess(result.message || `${user.email} was deleted successfully.`);
      setDeleteTarget(null);
    } catch (err) {
      setUsersError(adminApi.getErrorMessage(err, "The user account could not be deleted."));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  const ready = checks.every((check) => check.met);

  return (
    <div className="admin-users-page">
      <div className="admin-security-layout">
        <section className="admin-panel admin-security-card">
        <div className="admin-security-heading">
          <div><UserAddOutlined /></div>
          <span className="admin-eyebrow">Administrator access</span>
          <h2>Register a new user</h2>
          <p>Create login credentials for another trusted administrator. Share the password securely and ask them to change it after signing in.</p>
        </div>

        {error && <div className="admin-alert error" role="alert">{error}</div>}
        {success && <div className="admin-alert success" role="status"><CheckCircleFilled /><span>{success}</span></div>}

        <form onSubmit={submit} className="admin-security-form">
          <label className="admin-field admin-password-field">
            <span>Email address <b>*</b></span>
            <div><MailOutlined /><input type="email" value={form.email} onChange={(event) => update("email", event.target.value)} autoComplete="off" placeholder="Enter the new user's email address" required /></div>
            <small>This email will be used to sign in.</small>
          </label>

          <RegistrationPasswordField label="Temporary password" value={form.password} visible={visible.password} onChange={(value) => update("password", value)} onToggle={() => setVisible((current) => ({ ...current, password: !current.password }))} />
          <RegistrationPasswordField label="Confirm temporary password" value={form.confirmPassword} visible={visible.confirm} onChange={(value) => update("confirmPassword", value)} onToggle={() => setVisible((current) => ({ ...current, confirm: !current.confirm }))} />

          <div className="admin-password-checks">
            {checks.map((check) => <span className={check.met ? "met" : ""} key={check.label}><CheckCircleFilled /> {check.label}</span>)}
          </div>
          <button className="admin-primary-button admin-password-submit" disabled={!ready || saving}>
            {saving ? <><LoadingOutlined spin /> Creating account…</> : <><UserAddOutlined /> Create administrator</>}
          </button>
        </form>
        </section>

        <aside className="admin-security-note">
          <SafetyCertificateOutlined />
          <div><strong>Controlled access</strong><p>Only create accounts for trusted personnel. Password delivery, roles, and account deletion are not available through the current API, so credentials must be managed carefully.</p></div>
        </aside>
      </div>

      <section className="admin-panel admin-users-list-panel">
        <div className="admin-panel-header">
          <div><h3>Registered users</h3><p>{usersLoading ? "Loading accounts…" : `${users.length} administrator${users.length === 1 ? "" : "s"} registered`}</p></div>
          <button className="admin-secondary-button" onClick={loadUsers} disabled={usersLoading}><ReloadOutlined spin={usersLoading} /> Refresh</button>
        </div>
        <div className="admin-users-toolbar">
          <div className="admin-search"><SearchOutlined /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by email address…" /></div>
        </div>
        {usersError && <div className="admin-users-error"><div className="admin-alert error"><span>{usersError}</span><button onClick={loadUsers}>Try again</button></div></div>}
        {usersLoading ? <div className="admin-users-loading">{[1, 2, 3].map((item) => <div className="admin-skeleton user-row" key={item} />)}</div> : filteredUsers.length ? (
          <div className="admin-users-table" role="table" aria-label="Registered administrators">
            <div className="admin-users-table-head" role="row"><span>Email address</span><span>Registered</span><span>Last updated</span><span className="admin-users-action-heading">Action</span></div>
            {filteredUsers.map((user) => <div className="admin-users-row" role="row" key={user._id}>
              <div><span className="admin-user-avatar">{initials(user.email)}</span><div><strong>{user.email}</strong><small>Administrator</small></div></div>
              <span>{formatDate(user.createdAt)}</span><span>{formatDate(user.updatedAt)}</span>
              <button className="admin-user-delete" onClick={() => setDeleteTarget(user)} aria-label={`Delete ${user.email}`}><DeleteOutlined /><span>Delete</span></button>
            </div>)}
          </div>
        ) : <div className="admin-empty compact"><UserAddOutlined /><h3>{users.length ? "No matching users" : "No registered users found"}</h3><p>{users.length ? "Try another email address." : "Registered administrators will appear here."}</p></div>}
      </section>
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this user?"
        description={`This permanently removes ${deleteTarget?.email || "this user"} and revokes their ability to sign in. If this is your own account, you may lose future access after signing out.`}
        confirmLabel={deleting ? "Deleting…" : "Delete user"}
        onCancel={() => !deleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function initials(email = "") {
  return email.slice(0, 2).toUpperCase() || "AU";
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric" }).format(date);
}

function RegistrationPasswordField({ label, value, visible, onChange, onToggle }) {
  return <label className="admin-field admin-password-field"><span>{label} <b>*</b></span><div><LockOutlined /><input type={visible ? "text" : "password"} value={value} onChange={(event) => onChange(event.target.value)} autoComplete="new-password" placeholder={`Enter ${label.toLowerCase()}`} required /><button type="button" onClick={onToggle} aria-label={visible ? `Hide ${label}` : `Show ${label}`}>{visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}</button></div></label>;
}
